import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';

import dayjs from 'dayjs';

import { TypeUtilService } from '@core/services/utils/type-util.service';

import { TColors } from '@shared/models/item-icon-colors.model';
import { ProductCard, ProductMessage } from '@shared/models/product-card.model';
import { Card, Product, ProductTypes, ProductsList, ProductsListItem } from '@shared/models/products.model';
import { ShortProductCard } from '@shared/models/short-product-card.model';
import { AmountZeroFractionPipe } from '@shared/pipes/amount-zero-fraction.pipe';
import { AmountPipe } from '@shared/pipes/amount.pipe';
import { CardCurrencyPipe } from '@shared/pipes/card-currency.pipe';
import { CardIconPipe } from '@shared/pipes/card-icon.pipe';
import { CardNumberPipe } from '@shared/pipes/card-number.pipe';
import { MetalCodePipe } from '@shared/pipes/metal-code.pipe';
import { ProductIconPipe } from '@shared/pipes/product-icon.pipe';

import { ProductsUtilsService } from '../utils/products-utils.service';
import { CardAdditionalService } from './card-additional.service';
import { CardService } from './card.service';

@Injectable({
  providedIn: 'root'
})
export class ProductsTransformService {
  private readonly cardIconPipe = new CardIconPipe();
  private readonly cardNumberPipe = new CardNumberPipe();
  private readonly productIconPipe = new ProductIconPipe();
  private readonly cardCurrencyPipe = new CardCurrencyPipe();
  private readonly amountPipe = new AmountPipe();
  private readonly datePipe = new DatePipe('ru');
  private readonly amountZeroFractionPipe = new AmountZeroFractionPipe();
  private readonly metalCodePipe = new MetalCodePipe();

  constructor(
    private readonly cardService: CardService,
    private readonly cardAdditionalService: CardAdditionalService
  ) {
    dayjs.locale('ru');
  }

  /**
   * Генерация карточек продуктов
   */
  public generateProductCards(products: Product[], types: any[]): ProductCard[] {
    let productCardArray: ProductCard[] = [];

    types.forEach((type) => {
      if (type.type !== 'investment') {
        const typeProducts = products.filter((item: Product) => item.productType === type.type);

        const items = typeProducts.map((product: Product) => {
          const productCard: ProductCard = {
            endDate: this.getProductEndDate(product),
            status: product.status,
            message: this.getProductMessage(product),
            icon: this.getProductIcon(product, product.productType),
            id: product.id,
            balance: product.balance,
            currencyCode: product.currencyCode,
            type: product.productType,
            alias: product.alias,
            info: product.info,
            briefcase: product.briefcase || [],
            minimumAmount:
              product.creditInformation?.minimumAmount && +product.creditInformation.minimumAmount > 0
                ? +product.creditInformation.minimumAmount
                : 0,
            ...(product.productType === 'saveAccount' && {
              interestRate: product.interestRate,
              tariff: product.tariff
            })
          };

          if (product.productType !== 'credit') {
            productCard.number = product.productType === 'card' ? (product as Card).cardNumber : product.number;

            productCard.typeCaption =
              product.productType === 'card' ? product.cardTypeCaption || product.info : product.typeCaption;

            if (product.productType === 'card') {
              productCard.cardType = product.cardType;
            }
          }

          return productCard;
        });

        productCardArray = productCardArray.concat(items);
      }
    });

    return productCardArray;
  }

  /**
   * Генерация списка продуктов
   */
  public generateProductsList(products: Product[], types: any[]): ProductsList[] {
    return types
      .map((item: any): ProductsList => {
        return {
          title: item.title,
          type: item.type,
          show: false,
          items: this.generateProductsListItems(products, item.type)
        };
      })
      .filter((list: ProductsList) => list.items.length > 0);
  }

  /**
   * Генерация элементов списка продуктов
   */
  public generateProductsListItems(products: Product[], type: ProductTypes): ProductsListItem[] {
    const getProductsListItems = (
      products: Product[],
      type: ProductTypes,
      additional?: boolean
    ): ProductsListItem[] => {
      return products.map((product: Product) => {
        const interestRate = product.interestRate
          ? this.amountZeroFractionPipe.transform(product.interestRate.split('%')[0], 0) + '%'
          : null;
        const depositCaption =
          product.productType === 'deposit' && interestRate && product.endDate
            ? interestRate + ' • До ' + this.datePipe.transform(product.endDate, 'dd MMMM yyyy')
            : null;

        const interestAmountFact = product.interestAmountFact;
        const isDepositOrSave = type === 'deposit' || type === 'saveAccount';

        const productsListItem: ProductsListItem = {
          id: product.id,
          icon: this.getProductIcon(product, type),
          info: this.getHTMLProductInfo(product, type),
          caption: depositCaption,
          balance: product.balance,
          byDefault: product.byDefault,
          currencyCode: product.currencyCode,
          type,
          status: product.status,
          alias: product.alias,
          loanOverdue: product.loanOverdue,
          iconWithColor: product.iconWithColor,
          overduePrincipal: product.overduePrincipal,
          overdueTotal: product.overdueTotal,
          number: type === 'card' ? product.cardNumber : product.number,
          showInList: Object.prototype.hasOwnProperty.call(product, 'showInList') ? product.showInList : true,
          totalProfit: product.totalProfit,
          newMoneyInfo: {
            oldMoney: product?.oldMoney,
            newMoney: product?.newMoney
          },
          instrumentId: product.instrumentId,
          prohibitDupProgAcc: TypeUtilService.isTruthy(product.prohibitDupProgAcc)
        };

        // Явно добавляем interestAmountFact для депозитов и счетов с экономией
        if (isDepositOrSave && interestAmountFact) {
          productsListItem.interestAmountFact = interestAmountFact;
        }

        if (type === 'card') {
          const card = this.cardIconPipe.transform(+product.cardType);

          productsListItem.cardType = card?.type;
          productsListItem.color = card?.color as TColors;
          productsListItem.cardTypeCaption = product.cardTypeCaption || product.info;
          productsListItem.cardNumber = product.cardNumber;
          productsListItem.smsInformOn = (product as Card).smsInformOn;
          productsListItem.cardTypeCode = product.cardType;
        }

        if (additional && type === 'card') {
          productsListItem.additional = getProductsListItems((<Card>product).additionalCards || [], type);
        }

        return productsListItem;
      });
    };

    if (type === 'card') {
      const cards = this.getCardsListWithAdditionalCards(products);

      return getProductsListItems(cards, type, true);
    } else {
      const typeProducts = products.filter((item: Product) => item.productType === type);

      return getProductsListItems(typeProducts, type);
    }
  }

  /**
   * Генерация коротких карточек продуктов
   */
  public generateShortProductCards(products: Product[]): ShortProductCard[] {
    return products.map((product: Product) => {
      const productCard: ShortProductCard = {
        icon: this.getProductIcon(product, product.productType),
        info: product.info,
        id: product.id,
        balance: product.balance,
        currencyCode: product.currencyCode,
        currencyIsoCode: product.currency,
        currency: product.currency,
        type: product.productType,
        name: product.alias || product.info,
        number: product.productType === 'card' ? (product as Card).cardNumber : product.number,
        numberForCardFilter: product.number,
        vid: product.vid,
        accountNumber: product.number,
        cardType: product.productType === 'card' ? this.cardIconPipe.transform(+product.cardType)?.type : undefined,
        cardTypeCaption: product.productType === 'card' ? product.cardTypeCaption : undefined,
        cardNumber: product.cardNumber,
        generalCardNumber: product.generalCardNumber,
        typeCaption: product.typeCaption,
        alias: product.alias,
        sortNumber: this.getProductSortNumber(product, product.productType),
        subType: product.subtype,
        status: product.status,
        isDebitCard: product.isDebitCard,
        isBrokerAccount: product.isBrokerAccount,
        iconWithColor: product.iconWithColor,
        creditInformation: product.creditInformation,
        byDefault: product.byDefault,
        inComing: product.inComing,
        outGoing: product.outGoing,
        controlledByTime: (product as any)?.controlledByTime
      };

      return productCard;
    });
  }

  /**
   * Получение списка карт с дополнительными картами
   */
  public getCardsListWithAdditionalCards(products: Product[], enableBlockedProducts = false): Card[] {
    const allCards: Card[] = products.filter((item: Product) => item.productType === 'card') as Card[];
    const parentCards = allCards.filter((card) => card.generalCardNumber === card.cardNumber);
    const childrenCards = allCards.filter((child) => child.generalCardNumber !== child.cardNumber);
    let mappedCards = [];

    if (parentCards.length) {
      parentCards.forEach((parent) => {
        parent.additionalCards = childrenCards
          .filter((item) => item.generalCardNumber === parent.cardNumber)
          .map((card) => ({ ...card, showInList: !enableBlockedProducts && parent.status === 'block' }));
      });
      mappedCards = parentCards.reduce((acc, value) => {
        acc.push(value);
        if (value.additionalCards.length) {
          acc.push(...value.additionalCards);
        }

        return acc;
      }, []);
    } else {
      mappedCards = childrenCards.map((card) => ({ ...card, showInList: true }));
    }

    return mappedCards;
  }

  /**
   * Получение HTML-информации о продукте
   */
  public getHTMLProductInfo(product: Product, type: ProductTypes): string {
    const number = this.cardNumberPipe.transform(product.number || '', true);

    if (type === 'investment') {
      return this.getInfoByInvestmentAccount(product.info);
    }
    if (product.isMetallAccount) {
      const name = product.alias || 'Металлический счёт:';

      return `${name} <span class='bold-text'>${this.metalCodePipe.transform(product.currency)}${number}</span>`;
    }

    if (type === 'card') {
      const number = this.cardNumberPipe.transform(product.cardNumber, true);
      const name = product.alias || product.typeCaption;

      return `${name}<span class='bold-text'>${number}</span>`;
    }

    if (type === 'credit') {
      const currency = this.cardCurrencyPipe.transform(this.cardCurrencyPipe.transform(product.currencyCode));
      const amount = this.amountPipe.transform(product.creditInformation.nextPaymentAmount || 0);
      const number = product.creditInformation.contractNum;
      const date = this.datePipe.transform(product.creditInformation.nextPayDate || '', 'dd MMMM yyyy');

      return `${product.typeCaption} ${product.currency}<span class='bold-text'>: ${number}</span></br>
              <span class='brown-text'>${amount}</span> ${currency} <span class='bold-text'>•</span> До <span class='primary-text'>${date}</span>`;
    }

    if (type === 'account' && product.isBrokerAccount) {
      const name = product.alias || `Брокерский ${product.currency}`;

      return `${name} <span class='bold-text'>${number}</span>`;
    }

    if (type === 'deposit') {
      return `${product.alias || product.info}`;
    }

    if (type === 'saveAccount' || (type === 'account' && product.subtype === '15')) {
      const endDate = product.endDate
        ? "<span class='bold-text'>•</span> До <span class='primary-text'>" +
          this.datePipe.transform(product.endDate || '', 'dd MMMM yyyy') +
          '</span>'
        : '';
      const interestRate = this.amountZeroFractionPipe.transform(product.interestRate.split('%')[0], 0) + '%';
      const caption = `<br> <span class='bold-text'>${interestRate}</span> ${endDate}`;
      const name = product.alias || product.tariff;

      return `${name}: ${product.currency} <span class='bold-text'>${number}</span> ${caption}`;
    }

    const isDemandAccount = product.number.startsWith('42301');
    const caption = isDemandAccount ? 'До востребования' : product.typeCaption;

    return `${caption} ${product.currency} <span class='bold-text'>${number}</span>`;
  }

  /**
   * Получение даты окончания продукта
   */
  public getProductEndDate(product: Product): string {
    if (product.productType === 'saveAccount') {
      return product.endDate ? `до ${dayjs(product.endDate).format('D MMMM YYYY')}` : '';
    }
    if (product.productType === 'account') {
      return product.endDate ? `до ${dayjs(product.endDate).format('D MMMM YYYY')}` : '';
    }
    if (product.productType === 'investment') {
      return '';
    }

    return product.productType === 'card'
      ? `${dayjs(product.endDate).format('MM / YYYY')}`
      : `до ${dayjs(product.endDate).format('D MMMM YYYY')}`;
  }

  /**
   * Получение иконки продукта
   */
  public getProductIcon(product: Product, type: ProductTypes): string {
    if (!product) {
      return '';
    }

    switch (type) {
      case 'card':
        return `${this.cardIconPipe.transform(+product.cardType)?.value}`;

      case 'account':
        return product.subtype === '15'
          ? `save-account`
          : `${this.productIconPipe.transform(type, product.currencyCode)}`;

      case 'saveAccount':
        return `save-account`;

      case 'metallAccount':
        return `currency-metal`;

      case 'investment':
        return 'investment';

      default:
        return `${type}-card`;
    }
  }

  /**
   * Получение информации о продукте
   */
  public getProductInfo(product: Product, type: ProductTypes): string {
    if (!product) {
      return '';
    }
    let info = product.alias || product.info || product.typeCaption;

    if (type === 'card') {
      info =
        product.cardTypeCaption +
        ' ' +
        this.cardNumberPipe.transform((<Card>product).cardNumber) +
        ' <br> ' +
        product.number;
    }
    if (type === 'saveAccount') {
      info = product.tariff;
    }

    return info;
  }

  /**
   * Получение сообщения о продукте
   */
  public getProductMessage(product: Product): ProductMessage {
    if (['account', 'investment'].includes(product.productType)) return null;

    if (product.productType === 'card') {
      return this.getCardProductMessage(product as Card);
    }

    if (product.productType === 'credit') {
      return this.getCreditProductMessage(product);
    }

    if (product.productType === 'deposit') {
      const deposit = product;

      return deposit.interestRate ? { text: `Ставка ${deposit.interestRate}` } : null;
    }

    return null;
  }

  /**
   * Получение номера сортировки продукта
   */
  public getProductSortNumber(product: Product, type: ProductTypes): string {
    switch (type) {
      case 'card':
        return '1';

      case 'saveAccount':
        return '2';

      case 'account':
        if (product.currency === 'RUR') {
          return '3';
        }
        if (product.currency === 'USD') {
          return '4';
        }

        return '5';

      case 'deposit':
        return '6';

      default:
        return '0';
    }
  }

  /**
   * Получение заголовка продукта
   */
  public getProductTitle(product: Product, type: ProductTypes): string {
    const info = product.alias || product.info;

    switch (type) {
      case 'card':
        return product.typeCaption;

      case 'account':
        return this.getAccountTitle(product);

      case 'saveAccount':
        return `Накопительный счёт`;

      case 'credit':
        return `Кредит`;

      case 'deposit':
        return `Вклад`;

      case 'investment':
        return 'Инвестиции';

      default:
        return info;
    }
  }

  /**
   * Подготовка продуктов из данных сервера
   */
  public prepareProducts(data: any, isForSelect = false): Product[] {
    const products: Product[] = [];
    const localData = data ? { ...data } : {};

    Object.keys(localData).forEach((type: string) => {
      const currentTypeProducts = this.getArray(localData[type]);

      currentTypeProducts.forEach((product: any) => {
        product.isDebitCard = product.vid ? product.vid === '3' : false;
        product.isBrokerAccount = this.cardAdditionalService.checkOnBrokerAccount(product.number);
        product.isMetallAccount = ProductsUtilsService.isMetallAccount(product);
        product.isVirtual = TypeUtilService.isTruthy(product.isVirtual);
        product.isCollateralAccount = this.cardAdditionalService.checkOnCollateralAccount(product.subtype);
        product.tariff = product.subtype === '15' ? this.extractTariffPlanFromAccountInfo(product.info) : '';
        if (!isForSelect || (isForSelect && product.depositIsMove !== 'false')) products.push(product);
        product.productType = this.getProductType(type, product);
        product.loanOverdue = +product?.creditInformation?.overduePrincipal > 0 || +product?.overdueTotal > 0;
        product.iconWithColor = this.getIconWitColor(product);
        product.overduePrincipal = product.creditInformation?.overduePrincipal;
      });
    });

    return products;
  }

  /**
   * Получение заголовка счета
   */
  private getAccountTitle(product: Product): string {
    if (product.isBrokerAccount) return 'Брокерский счёт';
    if (product.isMetallAccount) return `Металл-счёт: ${this.metalCodePipe.transform(product.currency)}`;

    return 'Счёт';
  }

  /**
   * Преобразование массива
   */
  private getArray(data: any): any[] {
    if (!data) {
      return [];
    }
    if (Array.isArray(data)) {
      return data;
    }

    return [data];
  }

  /**
   * Получение информации из инвестиционного счета
   */
  private getInfoByInvestmentAccount(info: string): string {
    const reg = /Брокерский счет ИИС|Брокерский счет/;

    return info ? info.replace(reg, '').trim() : '';
  }

  private extractTariffPlanFromAccountInfo(info: string): string {
    return ProductsUtilsService.extractTariffPlanFromAccountInfo(info);
  }

  private getProductType(type: string, product: any) {
    let productType = type.substring(0, type.length - 1);

    if (product.isMetallAccount) {
      productType = 'metallAccount';
    }
    if (product.subtype === '15') {
      productType = 'saveAccount';
    }

    return productType;
  }

  private getIconWitColor(product: any) {
    const currencyData = ProductsUtilsService.getCurrencyDataByCode(product.currencyCode);

    switch (product.productType) {
      case 'credit':
        return {
          color: 'mint',
          icon: currencyData?.icon
        };

      case 'account':
        return {
          color: currencyData?.color,
          icon: currencyData?.icon
        };

      case 'deposit':
        return {
          color: 'purple',
          icon: 'deposit'
        };

      case 'investment':
        return {
          color: 'brown',
          icon: 'investment'
        };

      case 'metallAccount':
        return {
          color: currencyData?.color,
          icon: 'currency-metal'
        };

      case 'saveAccount':
        return {
          color: 'purple',
          icon: 'save-account'
        };

      case 'card':
        return {
          color: null,
          icon: null
        };
    }
  }

  private getCardProductMessage(card: Card): ProductMessage {
    if (!card.creditInformation?.nextPayDate) {
      return null;
    }

    const zeroAmount = +card.creditInformation.nextPaymentAmount === 0;
    const date = dayjs(card.creditInformation.nextPayDate).format('D MMMM YYYY');
    const currency = this.cardCurrencyPipe.transform(card.currencyCode);
    const text = zeroAmount
      ? 'Минимальный платёж оплачен'
      : `Платёж до ${date}: ${card.creditInformation.minimumAmount} ${currency}`;

    return {
      type: zeroAmount ? 'success' : 'error',
      text
    };
  }

  private getCreditProductMessage(credit: Product): ProductMessage {
    if (!credit.creditInformation?.nextPayDate) {
      return null;
    }

    const date = dayjs(credit.creditInformation.nextPayDate).format('D MMMM YYYY');
    const amount = this.amountPipe.transform(credit.creditInformation.nextPaymentAmount);
    const currency = this.cardCurrencyPipe.transform(credit.currencyCode);

    return {
      type: 'error',
      text: `Платёж до ${date}: ${amount} ${currency}`
    };
  }
}
